import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  Plus, Edit, Trash2, Star, StarOff, LogOut, 
  Building, Loader2, Upload, X, 
  Eye, Menu, MessageSquare, Image, Check, Link, Info
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_d28fa793-233b-4071-af00-df1c1e1b0bba/artifacts/6t4csq6x_Logo_Komel%20AG.png";

const categoryColors = {
  "Neubau": "bg-komel-newbuild",
  "Sanierung": "bg-komel-renovation",
  "Renovation": "bg-komel-refurbishment"
};

const DEFAULT_IMAGE = "https://images.pexels.com/photos/36065437/pexels-photo-36065437.jpeg";

// Helper to get cover image
const getCoverImage = (ref) => {
  if (ref.images && ref.images.length > 0) {
    const index = ref.coverImageIndex || 0;
    return ref.images[index] || ref.images[0];
  }
  return ref.image || DEFAULT_IMAGE;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout, getAuthHeader } = useAuth();
  const [references, setReferences] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("references");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Modal states
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRef, setSelectedRef] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    location: "",
    category: "Neubau",
    year: new Date().getFullYear().toString(),
    shortDescription: "",
    services: [],
    images: [],
    coverImageIndex: 0,
    pdfUrl: "",
    featured: false
  });
  const [serviceInput, setServiceInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState("");

  useEffect(() => {
    fetchReferences();
    fetchMessages();
  }, []);

  const fetchReferences = async () => {
    try {
      const response = await axios.get(`${API}/admin/references`, {
        headers: getAuthHeader()
      });
      setReferences(response.data);
    } catch (error) {
      console.error("Error fetching references:", error);
      if (error.response?.status === 401) {
        logout();
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/admin/messages`, {
        headers: getAuthHeader()
      });
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const openCreateModal = () => {
    setSelectedRef(null);
    setFormData({
      title: "",
      location: "",
      category: "Neubau",
      year: new Date().getFullYear().toString(),
      shortDescription: "",
      services: [],
      images: [],
      coverImageIndex: 0,
      pdfUrl: "",
      featured: false
    });
    setEditModal(true);
  };

  const openEditModal = (ref) => {
    setSelectedRef(ref);
    setFormData({
      title: ref.title,
      location: ref.location,
      category: ref.category,
      year: ref.year,
      shortDescription: ref.shortDescription,
      services: ref.services || [],
      images: ref.images || (ref.image ? [ref.image] : []),
      coverImageIndex: ref.coverImageIndex || 0,
      pdfUrl: ref.pdfUrl || "",
      featured: ref.featured
    });
    setEditModal(true);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Check max 6 images
    const remainingSlots = 6 - formData.images.length;
    if (files.length > remainingSlots) {
      toast.error(`Maximal 6 Bilder erlaubt. Sie können noch ${remainingSlots} Bilder hinzufügen.`);
      return;
    }

    setUploadingImage(true);

    try {
      const uploadPromises = files.map(async (file) => {
        const formDataUpload = new FormData();
        formDataUpload.append("file", file);
        const response = await axios.post(`${API}/admin/upload`, formDataUpload, {
          headers: {
            ...getAuthHeader(),
            "Content-Type": "multipart/form-data"
          }
        });
        return `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      setFormData(prev => ({ 
        ...prev, 
        images: [...prev.images, ...uploadedUrls]
      }));
      toast.success(`${uploadedUrls.length} Bild(er) hochgeladen`);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Fehler beim Hochladen");
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => {
      const newImages = prev.images.filter((_, i) => i !== index);
      // Adjust coverImageIndex if needed
      let newCoverIndex = prev.coverImageIndex;
      if (index === prev.coverImageIndex) {
        newCoverIndex = 0;
      } else if (index < prev.coverImageIndex) {
        newCoverIndex = prev.coverImageIndex - 1;
      }
      return {
        ...prev,
        images: newImages,
        coverImageIndex: Math.min(newCoverIndex, newImages.length - 1)
      };
    });
  };

  const setCoverImage = (index) => {
    setFormData(prev => ({ ...prev, coverImageIndex: index }));
    toast.success("Anzeigebild gesetzt");
  };

  const addImageUrl = () => {
    if (!imageUrlInput.trim()) {
      toast.error("Bitte eine URL eingeben");
      return;
    }
    
    if (formData.images.length >= 6) {
      toast.error("Maximal 6 Bilder erlaubt");
      return;
    }

    // Basic URL validation
    try {
      new URL(imageUrlInput.trim());
    } catch {
      toast.error("Ungültige URL");
      return;
    }

    setFormData(prev => ({
      ...prev,
      images: [...prev.images, imageUrlInput.trim()]
    }));
    setImageUrlInput("");
    toast.success("Bild-URL hinzugefügt");
  };

  const addService = () => {
    if (serviceInput.trim()) {
      setFormData(prev => ({
        ...prev,
        services: [...prev.services, serviceInput.trim()]
      }));
      setServiceInput("");
    }
  };

  const removeService = (index) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.location || !formData.shortDescription) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }

    setIsSubmitting(true);
    try {
      if (selectedRef) {
        await axios.put(`${API}/admin/references/${selectedRef.id}`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Referenz aktualisiert");
      } else {
        await axios.post(`${API}/admin/references`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Referenz erstellt");
      }
      setEditModal(false);
      fetchReferences();
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRef) return;

    setIsSubmitting(true);
    try {
      await axios.delete(`${API}/admin/references/${selectedRef.id}`, {
        headers: getAuthHeader()
      });
      toast.success("Referenz gelöscht");
      setDeleteModal(false);
      fetchReferences();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Fehler beim Löschen");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleFeatured = async (ref) => {
    try {
      await axios.patch(`${API}/admin/references/${ref.id}/featured`, {}, {
        headers: getAuthHeader()
      });
      fetchReferences();
      toast.success(ref.featured ? "Referenz verborgen" : "Referenz publiziert");
    } catch (error) {
      console.error("Toggle featured error:", error);
      toast.error("Fehler");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin");
  };

  const Sidebar = ({ mobile = false }) => (
    <div className={`${mobile ? 'p-4' : 'w-64 min-h-screen bg-white border-r border-komel-neutral-200 p-6'}`}>
      {!mobile && (
        <div className="mb-8">
          <img src={LOGO_URL} alt="Komel AG" className="h-10 w-auto" />
        </div>
      )}
      
      <nav className="space-y-2">
        <button
          onClick={() => { setActiveTab("references"); setMobileMenuOpen(false); }}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
            activeTab === "references" 
              ? "bg-komel-primary text-white" 
              : "text-komel-secondary hover:bg-komel-neutral-100"
          }`}
          data-testid="nav-references"
        >
          <Building className="w-5 h-5" />
          <span>Referenzen</span>
          <Badge className="ml-auto bg-komel-accent">{references.length}</Badge>
        </button>
        
        <button
          onClick={() => { setActiveTab("messages"); setMobileMenuOpen(false); }}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
            activeTab === "messages" 
              ? "bg-komel-primary text-white" 
              : "text-komel-secondary hover:bg-komel-neutral-100"
          }`}
          data-testid="nav-messages"
        >
          <MessageSquare className="w-5 h-5" />
          <span>Anfragen</span>
          <Badge className="ml-auto bg-komel-accent">{messages.length}</Badge>
        </button>
      </nav>

      <div className={`${mobile ? 'mt-6' : 'absolute bottom-6 left-6 right-6'}`}>
        <Button
          onClick={() => window.open("/", "_blank")}
          variant="outline"
          className="w-full mb-2"
          data-testid="view-site"
        >
          <Eye className="w-4 h-4 mr-2" />
          Website ansehen
        </Button>
        <Button
          onClick={handleLogout}
          variant="ghost"
          className="w-full text-komel-secondary hover:text-red-500"
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Abmelden
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-komel-neutral-100">
        <Loader2 className="w-12 h-12 animate-spin text-komel-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-komel-neutral-100 flex" data-testid="admin-dashboard">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block relative">
        <Sidebar />
      </div>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b z-40 px-4 py-3 flex items-center justify-between">
        <img src={LOGO_URL} alt="Komel AG" className="h-8 w-auto" />
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="w-6 h-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0">
            <Sidebar mobile />
          </SheetContent>
        </Sheet>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 lg:p-8 pt-20 lg:pt-8">
        {activeTab === "references" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <div>
                <h1 className="font-heading text-2xl lg:text-3xl font-bold text-komel-primary">
                  Referenzen verwalten
                </h1>
                <p className="text-komel-secondary mt-1">
                  {references.filter(r => r.featured).length} von {references.length} publiziert
                </p>
              </div>
              <Button
                onClick={openCreateModal}
                className="bg-komel-accent hover:bg-komel-accent-hover text-white"
                data-testid="add-reference"
              >
                <Plus className="w-5 h-5 mr-2" />
                Neue Referenz
              </Button>
            </div>

            {/* References Grid */}
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {references.map((ref) => (
                <div
                  key={ref.id}
                  className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                  data-testid={`reference-item-${ref.id}`}
                >
                  {/* Image */}
                  <div className="relative aspect-video">
                    <img
                      src={getCoverImage(ref)}
                      alt={ref.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-3 left-3 flex gap-2">
                      <Badge className={`${categoryColors[ref.category]} text-white`}>
                        {ref.category}
                      </Badge>
                      {ref.featured && (
                        <Badge className="bg-green-500 text-white">
                          <Check className="w-3 h-3 mr-1" />
                          Publiziert
                        </Badge>
                      )}
                    </div>
                    {/* Image count */}
                    {ref.images && ref.images.length > 1 && (
                      <div className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <Image className="w-3 h-3" />
                        {ref.images.length}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <h3 className="font-heading font-semibold text-komel-primary text-lg mb-1">
                      {ref.title}
                    </h3>
                    <p className="text-sm text-komel-secondary mb-3">
                      {ref.location} • {ref.year}
                    </p>
                    <p className="text-sm text-komel-secondary line-clamp-2 mb-4">
                      {ref.shortDescription}
                    </p>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={ref.featured ? "outline" : "default"}
                        onClick={() => toggleFeatured(ref)}
                        className={`flex-1 ${!ref.featured ? 'bg-green-500 hover:bg-green-600 text-white' : ''}`}
                        data-testid={`toggle-featured-${ref.id}`}
                      >
                        {ref.featured ? (
                          <><StarOff className="w-4 h-4 mr-1" /> Verbergen</>
                        ) : (
                          <><Check className="w-4 h-4 mr-1" /> Publizieren</>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditModal(ref)}
                        data-testid={`edit-reference-${ref.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-500 hover:bg-red-50"
                        onClick={() => { setSelectedRef(ref); setDeleteModal(true); }}
                        data-testid={`delete-reference-${ref.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {references.length === 0 && (
              <div className="bg-white rounded-lg p-12 text-center">
                <Building className="w-16 h-16 text-komel-neutral-300 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-komel-primary mb-2">
                  Keine Referenzen vorhanden
                </h3>
                <p className="text-komel-secondary mb-6">
                  Erstellen Sie Ihre erste Referenz, um sie auf der Website anzuzeigen.
                </p>
                <Button onClick={openCreateModal} className="bg-komel-accent hover:bg-komel-accent-hover">
                  <Plus className="w-5 h-5 mr-2" />
                  Erste Referenz erstellen
                </Button>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === "messages" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-komel-primary mb-8">
              Kontaktanfragen
            </h1>

            <div className="space-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className="bg-white rounded-lg p-6 shadow-sm"
                  data-testid={`message-${msg.id}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-semibold text-komel-primary">{msg.name}</h3>
                      <p className="text-sm text-komel-secondary">{msg.email}</p>
                      {msg.phone && <p className="text-sm text-komel-secondary">{msg.phone}</p>}
                    </div>
                    <Badge>{msg.subject}</Badge>
                  </div>
                  <p className="text-komel-secondary">{msg.message}</p>
                  <p className="text-xs text-komel-secondary mt-4">
                    {new Date(msg.createdAt).toLocaleString("de-CH")}
                  </p>
                </div>
              ))}

              {messages.length === 0 && (
                <div className="bg-white rounded-lg p-12 text-center">
                  <MessageSquare className="w-16 h-16 text-komel-neutral-300 mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-komel-primary mb-2">
                    Keine Anfragen vorhanden
                  </h3>
                  <p className="text-komel-secondary">
                    Neue Kontaktanfragen erscheinen hier.
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>

      {/* Edit/Create Modal */}
      <Dialog open={editModal} onOpenChange={setEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reference-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-komel-primary">
              {selectedRef ? "Referenz bearbeiten" : "Neue Referenz erstellen"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Title */}
            <div>
              <Label htmlFor="title">Titel *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="z.B. Mehrfamilienhaus Rosenberg"
                className="mt-1"
                data-testid="input-title"
              />
            </div>

            {/* Location & Year */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="location">Ort *</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="z.B. St. Gallen"
                  className="mt-1"
                  data-testid="input-location"
                />
              </div>
              <div>
                <Label htmlFor="year">Jahr</Label>
                <Input
                  id="year"
                  value={formData.year}
                  onChange={(e) => setFormData(prev => ({ ...prev, year: e.target.value }))}
                  placeholder="2024"
                  className="mt-1"
                  data-testid="input-year"
                />
              </div>
            </div>

            {/* Category */}
            <div>
              <Label>Kategorie</Label>
              <Select 
                value={formData.category} 
                onValueChange={(val) => setFormData(prev => ({ ...prev, category: val }))}
              >
                <SelectTrigger className="mt-1" data-testid="select-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Neubau">Neubau</SelectItem>
                  <SelectItem value="Sanierung">Sanierung</SelectItem>
                  <SelectItem value="Renovation">Renovation</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Kurzbeschreibung *</Label>
              <Textarea
                id="description"
                value={formData.shortDescription}
                onChange={(e) => setFormData(prev => ({ ...prev, shortDescription: e.target.value }))}
                placeholder="Beschreiben Sie das Projekt in 1-2 Sätzen..."
                className="mt-1"
                rows={3}
                data-testid="input-description"
              />
            </div>

            {/* Services */}
            <div>
              <Label>Leistungen</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={serviceInput}
                  onChange={(e) => setServiceInput(e.target.value)}
                  placeholder="Leistung hinzufügen..."
                  onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addService())}
                  data-testid="input-service"
                />
                <Button type="button" onClick={addService} variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.services.map((service, idx) => (
                  <Badge key={idx} variant="secondary" className="pr-1">
                    {service}
                    <button
                      onClick={() => removeService(idx)}
                      className="ml-2 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Image Upload - Multi-Image */}
            <div>
              <Label>Bilder (max. 6)</Label>
              
              {/* Info Box with OneDrive Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-2 mb-4">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-2">So teilen Sie Bilder über OneDrive:</p>
                    <ol className="list-decimal list-inside space-y-1 text-blue-700">
                      <li>Bild in OneDrive hochladen</li>
                      <li>Rechtsklick auf das Bild → <strong>"Einbetten"</strong> wählen</li>
                      <li>Auf <strong>"Generieren"</strong> klicken</li>
                      <li>Den Link aus dem HTML-Code kopieren (beginnt mit https://)</li>
                      <li>Link unten einfügen und "Hinzufügen" klicken</li>
                    </ol>
                    <p className="mt-2 text-xs text-blue-600">
                      Tipp: Nur den Link kopieren, der mit <code className="bg-blue-100 px-1 rounded">https://onedrive.live.com/embed?</code> beginnt
                    </p>
                  </div>
                </div>
              </div>

              {/* URL Input */}
              <div className="flex gap-2 mb-4">
                <Input
                  value={imageUrlInput}
                  onChange={(e) => setImageUrlInput(e.target.value)}
                  placeholder="Bild-URL einfügen (https://...)"
                  onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addImageUrl())}
                  data-testid="input-image-url"
                />
                <Button 
                  type="button" 
                  onClick={addImageUrl} 
                  variant="outline"
                  disabled={formData.images.length >= 6}
                >
                  <Link className="w-4 h-4 mr-1" />
                  Hinzufügen
                </Button>
              </div>

              <p className="text-sm text-komel-secondary mb-2">
                Klicken Sie auf ein Bild, um es als Anzeigebild festzulegen.
              </p>
              
              {/* Image Grid */}
              <div className="grid grid-cols-3 gap-3 mt-2">
                {formData.images.map((img, idx) => (
                  <div 
                    key={idx} 
                    className={`relative aspect-video rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                      idx === formData.coverImageIndex 
                        ? 'border-komel-accent ring-2 ring-komel-accent/30' 
                        : 'border-transparent hover:border-komel-secondary'
                    }`}
                    onClick={() => setCoverImage(idx)}
                  >
                    <img
                      src={img}
                      alt={`Bild ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = "https://placehold.co/400x300/e2e8f0/64748b?text=Bild+nicht+gefunden";
                      }}
                    />
                    {idx === formData.coverImageIndex && (
                      <div className="absolute top-1 left-1 bg-komel-accent text-white text-xs px-2 py-0.5 rounded flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Anzeige
                      </div>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); removeImage(idx); }}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                
                {/* Empty state */}
                {formData.images.length === 0 && (
                  <div className="col-span-3 aspect-video rounded-lg border-2 border-dashed border-komel-neutral-300 flex flex-col items-center justify-center text-komel-secondary">
                    <Image className="w-10 h-10 mb-2 opacity-50" />
                    <span className="text-sm">Noch keine Bilder hinzugefügt</span>
                  </div>
                )}
              </div>
              
              <p className="text-xs text-komel-secondary mt-2">
                {formData.images.length}/6 Bilder
              </p>
            </div>

            {/* PDF URL */}
            <div>
              <Label htmlFor="pdfUrl">PDF-Link (optional)</Label>
              <Input
                id="pdfUrl"
                value={formData.pdfUrl}
                onChange={(e) => setFormData(prev => ({ ...prev, pdfUrl: e.target.value }))}
                placeholder="https://..."
                className="mt-1"
                data-testid="input-pdf"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-komel-primary hover:bg-komel-primary-light"
              data-testid="submit-reference"
            >
              {isSubmitting ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Speichern...</>
              ) : (
                selectedRef ? "Aktualisieren" : "Erstellen"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
        <DialogContent data-testid="delete-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-komel-primary">
              Referenz löschen
            </DialogTitle>
            <DialogDescription>
              Sind Sie sicher, dass Sie "{selectedRef?.title}" löschen möchten? 
              Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleDelete}
              disabled={isSubmitting}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Löschen"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
